import { Mutex } from 'async-mutex';
import axios, { AxiosRequestConfig } from 'axios';
import { isEmpty, isNil } from 'lodash';
import { stringify } from 'qs';
import { useRecoilValue } from 'recoil';

import { userProfileAtom } from '@store/atoms/user';
import { workspaceAtom } from '@store/atoms/workspace';
import { getAuthToken, getClientUuidFromRoute } from '@utils/auth';

import { HttpMethod } from './enums';
import { handleError } from './http-client-util';

const paramsSerializer = (params: any) =>
  stringify(params, { arrayFormat: 'repeat' });

const mutex = new Mutex();
interface CreateHttpClientProps {
  allowWithCredentials?: boolean;
  chooseLastClientUuid?: boolean;
}

export const createHttpClient = (props: CreateHttpClientProps = {}) => {
  const client = useRecoilValue(workspaceAtom);
  const clientUuid = client?.uuid || getClientUuidFromRoute() || '';

  const { allowWithCredentials = true, chooseLastClientUuid } = props;
  const instance = axios.create({ withCredentials: allowWithCredentials });

  const user = useRecoilValue(userProfileAtom);

  const headers = {};

  const requestWithRetry = async <R>({
    url,
    method = HttpMethod.GET,
    params,
    data,
    onUploadProgress,
  }: AxiosRequestConfig) => {
    if (allowWithCredentials) {
      await mutex.waitForUnlock();
      const token = getAuthToken({
        clientUuid,
        chooseLastClientUuid,
        ignoreSingleClientToken: false,
      });

      if (!isNil(token)) {
        Object.assign(headers, {
          Authorization: `Bearer ${token}`,
        });
      }
    }

    const requestPayload = {
      method,
      baseURL: "https://api.brandtechbrain.com/api/",
      ...(!isEmpty(headers) && { headers }),
      url,
      data,
      params,
      paramsSerializer,
      onUploadProgress,
    };

    try {
      const response = await instance.request<R>(requestPayload);

      return response.data as R;
    } catch (error: any) {
      const response = await handleError({
        error,
        mutex,
        httpClient: {
          requestPayload,
          axiosInstance: instance,
        },
        clientData: { clientUuid, userId: user?.id },
      });

      return response;
    }
  };

  return requestWithRetry;
};
